import React, { useState } from 'react';
import { api } from '../api';
import { FileText, Sparkles, Loader2, Download, Copy, RefreshCw, FileDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

function MarkdownRenderer({ content }) {
  const lines = content.split('\n');
  return (
    <div className="prose prose-sm max-w-none text-sm leading-relaxed">
      {lines.map((line, i) => {
        const k = `${i}-${line.slice(0, 24)}`;
        if (line.startsWith('# ')) return <h1 key={k} className="text-xl font-bold mt-4 mb-2 text-[#0F392B]">{line.slice(2)}</h1>;
        if (line.startsWith('## ')) return <h2 key={k} className="text-base font-bold mt-4 mb-1.5 text-[#0F392B] uppercase tracking-wide border-b border-[#0F392B] pb-1">{line.slice(3)}</h2>;
        if (line.startsWith('### ')) return <h3 key={k} className="text-sm font-semibold mt-3 mb-1">{line.slice(4)}</h3>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <li key={k} className="ml-4 text-xs mb-0.5 list-disc">{line.slice(2)}</li>;
        if (line.startsWith('**') && line.endsWith('**')) return <p key={k} className="font-semibold text-xs mt-2">{line.slice(2, -2)}</p>;
        if (line.trim() === '') return <div key={k} className="h-2" />;
        if (line.startsWith('---')) return <hr key={k} className="my-2 border-[#E2E2E2]" />;
        return <p key={k} className="text-xs mb-0.5">{line}</p>;
      })}
    </div>
  );
}

export default function ResumePage() {
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await api.generateResume({ style: 'harvard' });
      setResume(result);
      if (result.error) {
        toast.error('Resume generated with limited data. Add more profile info for better results.');
      } else {
        toast.success('Resume generated');
      }
    } catch (err) { toast.error(err.message || 'Generation failed'); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (resume?.resume) {
      navigator.clipboard.writeText(resume.resume);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!resume?.resume) return;
    const blob = new Blob([resume.resume], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resume.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  const handlePdfExport = async () => {
    setPdfLoading(true);
    try {
      const blob = await api.downloadResumePdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resume.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('PDF export failed'); }
    finally { setPdfLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="resume-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Resume Builder
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            AI-powered resume built from your profile and activities
          </p>
        </div>
        <Button onClick={handleGenerate} disabled={loading}
          className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8" data-testid="generate-resume-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
          {resume ? 'Regenerate' : 'Generate Resume'}
        </Button>
      </div>

      {!resume && !loading && (
        <Card className="border-[#E2E2E2]">
          <CardContent className="p-8 text-center space-y-4">
            <FileText className="h-16 w-16 text-muted-foreground/20 mx-auto" strokeWidth={1} />
            <div>
              <h2 className="text-base font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                Harvard-Style Resume Generator
              </h2>
              <p className="text-xs text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                Generate a polished, admissions-ready resume using your profile data, activities, 
                and academic information. Modeled after Harvard career services resume format.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {['Profile Info', 'Activities', 'Test Scores', 'Coursework'].map(item => (
                <Badge key={item} variant="outline" className="text-[10px]">{item}</Badge>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Tip: Complete your Profile and Activities sections first for the best results.
            </p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="border-[#E2E2E2]">
          <CardContent className="p-12 text-center space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-[#0F392B] mx-auto" />
            <p className="text-sm text-muted-foreground">Generating your resume...</p>
            <p className="text-xs text-muted-foreground">Analyzing your profile, activities, and academics</p>
          </CardContent>
        </Card>
      )}

      {resume && !loading && (
        <>
          {/* Actions bar */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleCopy} className="text-xs h-7" data-testid="copy-resume-btn">
              <Copy className="h-3 w-3 mr-1" /> {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} className="text-xs h-7" data-testid="download-resume-btn">
              <Download className="h-3 w-3 mr-1" /> Download .md
            </Button>
            <Button size="sm" variant="outline" onClick={handlePdfExport} disabled={pdfLoading} className="text-xs h-7" data-testid="export-resume-pdf-btn">
              {pdfLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />} Export PDF
            </Button>
            <Button size="sm" variant="outline" onClick={handleGenerate} className="text-xs h-7" data-testid="regenerate-resume-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Regenerate
            </Button>
            {resume.sections_used && (
              <div className="ml-auto flex gap-1">
                {resume.sections_used.map(s => (
                  <Badge key={s} className="bg-[#0F392B]/5 text-[#0F392B] border-[#0F392B]/10 text-[9px] capitalize">{s}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* Resume Content */}
          <Card className="border-[#E2E2E2] shadow-sm">
            <CardContent className="p-8 bg-white" data-testid="resume-content">
              <MarkdownRenderer content={resume.resume} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
